import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShoppingCart, ChevronRight } from 'lucide-react';
import usePlanningHub from '../hooks/usePlanningHub';
import BookingBar from '../components/planning/BookingBar';
import ServiceGrid from '../components/planning/ServiceGrid';
import CartSidebar from '../components/planning/CartSidebar';
import ReviewSection from '../components/planning/ReviewSection';
import ServiceDetailModal from '../components/planning/ServiceDetailModal';
import PackageDetailModal from '../components/planning/PackageDetailModal';
import EmailQuoteModal from '../components/planning/EmailQuoteModal';
import CheckoutModal from '../components/planning/CheckoutModal';

export default function EventPlanningHub() {
  const hub = usePlanningHub();
  const { isEventDetailsComplete, cart, quote, showReview, setShowReview, navigate } = hub;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight mb-2" data-testid="hub-title">
            Event Planning Hub
          </h1>
          <p className="text-muted-foreground text-sm sm:text-lg">
            Plan your perfect event with instant pricing and availability
          </p>
        </div>

        {/* Booking Bar */}
        <BookingBar hub={hub} />

        {/* Empty State — but ALSO show cart if user has already added items
            (e.g. arrived from a product detail page). */}
        {!isEventDetailsComplete && (
          <Card className="max-w-2xl mx-auto text-center py-8 sm:py-12 mb-8">
            <CardContent>
              <div className="text-4xl sm:text-6xl mb-4">📅</div>
              <h2 className="text-lg sm:text-xl font-semibold mb-2">
                {cart.length > 0 ? `${cart.length} item${cart.length > 1 ? 's' : ''} in your cart — set your event details to see pricing` : 'Enter your event details'}
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                {cart.length > 0
                  ? 'Add the date and venue zip above so we can quote your event.'
                  : 'Fill in the date and location above to see available services'}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                <Button variant="outline" onClick={() => navigate('/venues')}>Browse Venues</Button>
                <Button variant="outline" onClick={() => navigate('/events/denver')}>See Denver Events</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cart sidebar always visible when cart has items, even before event
            details are complete, so users can see what they added. */}
        {!isEventDetailsComplete && cart.length > 0 && (
          <div className="max-w-md mx-auto">
            <CartSidebar hub={hub} />
          </div>
        )}

        {/* Main Content: Service Grid + Cart Sidebar */}
        {isEventDetailsComplete && !showReview && (
          <div className="flex gap-4 sm:gap-6">
            <ServiceGrid hub={hub} />
            <CartSidebar hub={hub} />
          </div>
        )}

        {/* Mobile Cart Button */}
        {cart.length > 0 && !showReview && (
          <div className="fixed bottom-4 left-4 right-4 lg:hidden z-40">
            <Button className="w-full h-14 text-base shadow-xl" onClick={() => setShowReview(true)} data-testid="mobile-cart-btn">
              <ShoppingCart className="mr-2 h-5 w-5" />
              Review Event ({cart.length} items) {quote && `— $${quote.total.toFixed(2)}`}
              <ChevronRight className="ml-auto h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Review Section */}
        {showReview && quote && <ReviewSection hub={hub} />}
      </div>

      {/* Modals */}
      <ServiceDetailModal hub={hub} />
      <PackageDetailModal hub={hub} />
      <EmailQuoteModal hub={hub} />
      <CheckoutModal hub={hub} />
    </div>
  );
}
